/**
 * Chapitre 3 — réparer le programme d'arrosage, puis l'éprouver sur une semaine.
 *
 * Deux compétences du programme de 4e sont visées, et elles vont dans cet
 * ordre : **C6**, comprendre et modifier un programme existant, puis **C9**,
 * le mettre au point en le testant. C'est pourquoi l'îlot ne part pas d'une
 * page blanche : il reçoit le programme que la ville utilise aujourd'hui, celui
 * qui arrose à midi sans rien mesurer, et le corrige.
 *
 * Comme au chapitre 2, plusieurs réponses tiennent debout et ne se valent pas :
 * arroser à 6 h du matin n'est pas faux, c'est seulement moins bon que le soir.
 * D'où la même grammaire que `solutions` — `qualite`, `etoiles`, `feedback`.
 */
import type {
  Condition,
  Content,
  JourneeMeteo,
  Programme,
  ReglageProgramme,
  VarianteProgramme,
} from './content-schema.js';
import type { ActionResult, GameState } from './state.js';
import { refus, withAction } from './state.js';

/* ------------------------------------------------------------------ */
/* Le programme en cours                                               */
/* ------------------------------------------------------------------ */

/** Le programme que ce chapitre fait réparer, s'il y en a un. */
export function programmeDuChapitre(content: Content, chapterId: string): Programme | undefined {
  return content.programmes.find((p) => p.chapitre === chapterId);
}

/**
 * Le réglage courant : celui de l'îlot s'il y a touché, sinon celui de la ville.
 *
 * C'est ce qui permet d'afficher le programme existant avant toute
 * modification — l'étape « Comprendre » repose entièrement là-dessus.
 */
export function reglageCourant(state: GameState, content: Content): ReglageProgramme | null {
  const programme = programmeDuChapitre(content, state.chapterId);
  if (!programme) return null;
  return state.programme ?? programme.depart;
}

/** L'îlot a-t-il modifié quoi que ce soit ? */
export function programmeModifie(state: GameState, content: Content): boolean {
  const programme = programmeDuChapitre(content, state.chapterId);
  if (!programme || state.programme === null) return false;
  return !memeReglage(state.programme, programme.depart);
}

const memeCondition = (a: Condition | null, b: Condition | null): boolean =>
  a === null || b === null
    ? a === b
    : a.grandeur === b.grandeur && a.comparateur === b.comparateur && a.seuil === b.seuil;

const memeReglage = (a: ReglageProgramme, b: ReglageProgramme): boolean =>
  a.heure === b.heure &&
  a.duree_minutes === b.duree_minutes &&
  memeCondition(a.condition, b.condition);

/* ------------------------------------------------------------------ */
/* Étape 2 — Réparer                                                   */
/* ------------------------------------------------------------------ */

/**
 * Enregistre un réglage, après avoir vérifié qu'il reste dans ce que le contenu
 * propose.
 *
 * Les listes sont fermées à dessein : on veut que l'élève raisonne sur « le soir
 * plutôt que midi », pas qu'il cherche la minute exacte. Un réglage hors liste
 * ne peut venir que d'un écran trafiqué, et c'est donc un refus technique.
 */
export function setProgramme(
  state: GameState,
  content: Content,
  reglage: ReglageProgramme,
): ActionResult {
  const programme = programmeDuChapitre(content, state.chapterId);
  if (!programme) {
    return refus(
      'probleme_inconnu',
      `Aucun programme à régler au chapitre « ${state.chapterId} ».`,
    );
  }

  const { heures, durees_minutes, grandeurs, seuils } = programme.reglages;

  if (!heures.includes(reglage.heure)) {
    return refus('choix_inconnu', `Heure hors des valeurs proposées : ${reglage.heure}.`);
  }
  if (!durees_minutes.includes(reglage.duree_minutes)) {
    return refus('choix_inconnu', `Durée hors des valeurs proposées : ${reglage.duree_minutes}.`);
  }
  if (reglage.condition !== null) {
    const { grandeur, seuil } = reglage.condition;
    if (!grandeurs.some((g) => g.id === grandeur)) {
      return refus('choix_inconnu', `Grandeur inconnue du programme : « ${grandeur} ».`);
    }
    if (!seuils.includes(seuil)) {
      return refus('choix_inconnu', `Seuil hors des valeurs proposées : ${seuil}.`);
    }
  }

  return {
    ok: true,
    state: withAction({ ...state, programme: reglage }, { type: 'regler_programme', reglage }),
  };
}

/* ------------------------------------------------------------------ */
/* Évaluation                                                          */
/* ------------------------------------------------------------------ */

export interface EvaluationProgramme {
  readonly variante: VarianteProgramme | null;
  readonly message: string;
  /** Vrai tant que l'îlot n'a pas touché au programme de la ville. */
  readonly inchange: boolean;
}

/**
 * Que vaut le réglage courant ?
 *
 * Aucune variante ne correspond → on le dit sans juger : c'est la simulation de
 * la semaine qui montrera ce qui cloche, pas une sentence.
 */
export function evaluateProgramme(state: GameState, content: Content): EvaluationProgramme {
  const programme = programmeDuChapitre(content, state.chapterId);
  if (!programme) {
    return { variante: null, message: '', inchange: true };
  }

  const reglage = state.programme ?? programme.depart;
  const inchange = state.programme === null || memeReglage(reglage, programme.depart);
  const variante = programme.variantes.find((v) => memeReglage(v.reglage, reglage)) ?? null;

  return {
    variante,
    message: variante?.feedback ?? programme.feedback_inconnu,
    inchange,
  };
}

/* ------------------------------------------------------------------ */
/* Étape 3 — Tester : une semaine de météo                             */
/* ------------------------------------------------------------------ */

export interface JourneeSimulee {
  readonly jour: string;
  readonly temperature: number;
  readonly pluie_mm: number;
  /** Humidité du sol au matin, avant la journée. */
  readonly humiditeMatin: number;
  readonly arrose: boolean;
  /** Litres réellement sortis de la vanne. */
  readonly litres: number;
  /** Part de ces litres perdue par évaporation, si l'arrosage a lieu en pleine chaleur. */
  readonly litresPerdus: number;
  readonly humiditeSoir: number;
  /** Les plantes ont-elles manqué d'eau ce jour-là ? */
  readonly plantesEnSouffrance: boolean;
}

export interface SemaineSimulee {
  readonly jours: readonly JourneeSimulee[];
  readonly litresTotal: number;
  readonly litresPerdusTotal: number;
  readonly joursEnSouffrance: number;
}

/** L'arrosage a-t-il lieu ce jour-là, d'après la condition du programme ? */
function declenche(reglage: ReglageProgramme, humidite: number, temperature: number): boolean {
  if (reglage.duree_minutes === 0) return false;
  if (reglage.condition === null) return true;

  const mesure = reglage.condition.grandeur === 'temperature' ? temperature : humidite;
  return reglage.condition.comparateur === 'inferieur'
    ? mesure < reglage.condition.seuil
    : mesure > reglage.condition.seuil;
}

/**
 * Déroule la semaine décrite par le contenu, jour après jour.
 *
 * Le modèle est volontairement simple, mais il porte la leçon : la pluie remplit
 * le sol, la chaleur l'assèche, et l'eau versée en pleine journée s'évapore en
 * partie — elle est payée sans arriver aux racines.
 *
 * L'humidité du matin vient de la météo du contenu et non du jour précédent :
 * chaque îlot éprouve ainsi exactement la même semaine, et deux groupes peuvent
 * comparer leurs programmes sur les mêmes situations.
 */
export function simulateSemaine(state: GameState, content: Content): SemaineSimulee {
  const programme = programmeDuChapitre(content, state.chapterId);
  if (!programme) {
    return { jours: [], litresTotal: 0, litresPerdusTotal: 0, joursEnSouffrance: 0 };
  }

  const reglage = state.programme ?? programme.depart;
  const s = programme.simulation;

  /*
   * Quelle part de l'eau n'atteint jamais les racines ?
   *
   * Le soir, rien : l'eau s'infiltre toute la nuit. En pleine journée,
   * l'essentiel. Tôt le matin, une part faible mais réelle — il fait frais à
   * 6 h, mais l'eau reste en surface pendant la chaleur qui suit. C'est ce qui
   * sépare le matin du soir, et donc deux étoiles de trois.
   */
  const partEvaporee =
    reglage.heure >= s.heure_fraiche_soir
      ? 0
      : reglage.heure <= s.heure_fraiche_matin
        ? s.evaporation_le_matin
        : s.evaporation_en_journee;

  const jours = content.meteo_semaine.map((meteo: JourneeMeteo): JourneeSimulee => {
    const apresPluie = Math.min(100, meteo.humidite_sol_matin + meteo.pluie_mm * 2);
    const arrose = declenche(reglage, apresPluie, meteo.temperature);

    const litres = arrose ? reglage.duree_minutes * s.litres_par_minute : 0;
    const litresPerdus = arrose ? litres * partEvaporee : 0;

    const gainBrut = arrose ? reglage.duree_minutes * s.gain_humidite_par_minute : 0;
    const gain = gainBrut * (1 - partEvaporee);
    const perte = meteo.temperature >= s.seuil_chaud_celsius ? s.perte_si_chaud : s.perte_sinon;

    const humiditeSoir = Math.max(0, Math.min(100, apresPluie + gain - perte));

    return {
      jour: meteo.jour,
      temperature: meteo.temperature,
      pluie_mm: meteo.pluie_mm,
      humiditeMatin: meteo.humidite_sol_matin,
      arrose,
      litres,
      litresPerdus,
      humiditeSoir,
      plantesEnSouffrance: humiditeSoir < s.humidite_min_plantes,
    };
  });

  return {
    jours,
    litresTotal: jours.reduce((total, j) => total + j.litres, 0),
    litresPerdusTotal: jours.reduce((total, j) => total + j.litresPerdus, 0),
    joursEnSouffrance: jours.filter((j) => j.plantesEnSouffrance).length,
  };
}
